import { stAnalytics } from "@storytell/analytics";
import { GetSubscriptionRequestConfig, type Subscription, useStorytellClient } from "@storytell/client";
import { timestampToDate, useCallOptions, useTenancyClient, withBearerToken } from "@storytell/cp-client";
import { PlanType } from "@storytell/cp-client/src/gen/tenancy/v3/tenancy_pb";
import { subscribeToGlobalEvent } from "@storytell/events";
import { createEffect, createResource, createSignal, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";

export type BillingService = ReturnType<typeof createBillingService>;

const fromSubscription = (orgId: string, subscription: Subscription | null) => ({
  organizationId: orgId,
  canManageBilling: subscription?.canManageBilling ?? false,
  billingInterval: subscription?.billingInterval,
  customTerms: subscription?.customTerms,
});

export const createBillingService = ({
  authReady,
  authToken,
  authUserId,
  authRegistered,
  hasChats,
  userId,
  organizationId,
}: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  authUserId: () => string | undefined;
  authRegistered: () => boolean;
  hasChats: () => boolean;
  userId: () => string | undefined;
  /** The organization whose plan and balance every reader here describes. */
  organizationId: () => string | undefined;
}) => {
  const tenancyClient = useTenancyClient();
  const client = useStorytellClient();
  const [show, setShow] = createSignal(false);

  // The organization id is the resource source rather than a readiness flag: a
  // boolean never changes when the organization does, so a switch would keep
  // serving the previous one's plan and balance.
  const billedOrganizationId = () => {
    if (!authReady() || !userId() || !(authRegistered() || hasChats())) return undefined;
    return organizationId();
  };

  const [data, { refetch }] = createResource(
    billedOrganizationId,
    async (orgId) => {
      setShow(true);
      const res = await tenancyClient.getTenancyInfo(
        { organizationId: orgId },
        useCallOptions(withBearerToken(authToken()))
      );

      if (res.isOk()) {
        const { balance, plan } = res.value.tenancyInfo;
        return {
          organizationId: orgId,
          balance: {
            ...balance,
            lastUpdated: timestampToDate(balance.lastUpdated),
            credits: Number(balance.credits),
            creditsAddedThisPeriod: Number(balance.creditsAddedThisPeriod),
            creditsUsedThisPeriod: Number(balance.creditsUsedThisPeriod),
          },
          plan: {
            ...plan,
            createdAt: timestampToDate(plan.createdAt),
            modifiedAt: timestampToDate(plan.modifiedAt),
            // Anything unrecognised lands on free, so every paid plan the
            // backend can send has to be named here or its subscribers are
            // billed for a tier the app then tells them they don't have.
            type:
              plan.type === PlanType.PRO_V1
                ? "PLAN_TYPE_PRO_V1"
                : plan.type === PlanType.PRO_V2
                  ? "PLAN_TYPE_PRO_V2"
                  : plan.type === PlanType.TEAM_V1
                    ? "PLAN_TYPE_TEAM_V1"
                    : plan.type === PlanType.POWER_USER_V1
                      ? "PLAN_TYPE_POWER_USER_V1"
                      : "PLAN_TYPE_FREE_V1",
          },
        };
      }

      return undefined;
    },
    { initialValue: undefined }
  );

  const [subscriptionData, { mutate: mutateSubscription }] = createResource(
    // Mirror data's source so an org switch refetches; otherwise .latest
    // keeps serving the previous org's canManageBilling after the org changes.
    billedOrganizationId,
    async (orgId) => {
      // The organization is named rather than left to the ambient scope: this
      // store follows whichever organization the caller is billed under, not
      // the URL's.
      const res = await client.fetch(GetSubscriptionRequestConfig.withBearerToken(authToken()), undefined, undefined, {
        orgIdOverride: orgId,
      });
      return res.isOk() ? fromSubscription(orgId, res.value.subscription) : undefined;
    },
    { initialValue: undefined }
  );

  // Read .latest, not data(): these run on every render, and a suspending read
  // would pin the nearest Suspense on billing's cold-tab fetch. .latest outlives
  // a source change, so drop it once a different organization is in view. With
  // no organization the fetch is only paused (auth handshake), so the last
  // values still describe the right one and are kept.
  const billed = () => {
    const orgId = organizationId();
    return orgId === undefined || data.latest?.organizationId === orgId ? data.latest : undefined;
  };

  const credits = () => billed()?.balance.credits ?? 0;

  // Both Pro versions are the same tier to every reader of this: the version
  // only decides the price and the allocation.
  const isProPlan = () => {
    const type = billed()?.plan.type;
    return type === "PLAN_TYPE_PRO_V1" || type === "PLAN_TYPE_PRO_V2";
  };
  const isPowerUserPlan = () => billed()?.plan.type === "PLAN_TYPE_POWER_USER_V1";
  const isFreePlan = () => billed()?.plan.type === "PLAN_TYPE_FREE_V1";
  // Undefined until the plan loads, and a guest's never does, so both read as unpaid.
  const isPaidPlan = () => billed() !== undefined && !isFreePlan();
  // The plan enum's own name, for a reader the predicates above cannot answer:
  // what a credit top-up earns turns on which version of Pro the organization
  // is on, and those two are one plan to every other reader here.
  const planType = () => billed()?.plan.type;
  // .latest outlives a source change, and pairing a previous organization's
  // annual period or custom terms with this one's plan would quote a bonus
  // nobody earns.
  const subscription = () => {
    const orgId = organizationId();
    return orgId === undefined || subscriptionData.latest?.organizationId === orgId
      ? subscriptionData.latest
      : undefined;
  };
  const billingInterval = () => subscription()?.billingInterval;
  // Undefined until the organization has answered and null once it is known to
  // be on catalog terms, so a reader can hold back a catalog claim until then.
  // A refused fetch reads as null: only an owner gets an answer, and every
  // surface that shows the terms is an owner's.
  const customTerms = () => {
    if (subscriptionData.loading) return undefined;
    const answered = subscription();
    if (answered === undefined && billedOrganizationId() === undefined) return undefined;
    return answered?.customTerms ?? null;
  };
  // The plan, period and custom-terms rate a credit top-up's bonus is priced
  // from, paired so a reader cannot combine a settled plan with a period still
  // in flight, and undefined until both fetches have answered for the
  // organization in view.
  //
  // A reader that names the resulting credit figure needs pending told apart
  // from an answer: the accessors above read .latest and never suspend, so
  // before the fetches land an organization that earns a bonus reads as one
  // that earns none, and a figure printed then is silently corrected a moment
  // later. A refused subscription fetch is an answer, not pending — it reports
  // no period, which prices the top-up as monthly.
  const topUpPricing = () => {
    const billing = billed();
    if (billing === undefined || subscriptionData.loading) return undefined;
    return {
      planType: billing.plan.type,
      billingInterval: billingInterval(),
      termsMultiplier: subscription()?.customTerms?.topUpMultiplier,
    };
  };

  const planDisplayName = () => billed()?.plan.displayName ?? "Free";
  const loading = () => data.loading;
  const creditsUsedThisPeriod = () => billed()?.balance.creditsUsedThisPeriod ?? 0;

  // A guest who has started a chat has a balance too, but identifying one gives an anonymous
  // visitor a person profile, and an account signed in to later would never be merged into it.
  createEffect(() => {
    const d = billed();
    const id = authUserId();
    if (!d || !id || !authRegistered()) return;

    void stAnalytics.identify(id, {
      planType: d.plan.type,
      creditBalance: d.balance.credits,
    });
  });

  if (!isServer) {
    onMount(() => {
      const unsubscribeAuthRefresh = subscribeToGlobalEvent("auth-token-refreshed", () => {
        void refetch();
      });

      onCleanup(() => {
        unsubscribeAuthRefresh();
      });
    });
  }

  // Undefined only while the answer is in flight. A reader that only shows or
  // hides a control can treat that as false; one that swaps the control's action
  // on the answer has to tell pending apart from a definite no. A refused fetch
  // is a no: a non-owner is rejected by the endpoint.
  const canManageBilling = () => {
    if (subscriptionData.loading) return undefined;
    return subscriptionData.latest?.canManageBilling ?? false;
  };

  // Stores the subscription a plan change answered with instead of refetching:
  // while a refetch is in flight the terms read as unknown, which blanks the plan card.
  const applySubscription = (updated: Subscription) => {
    const orgId = billedOrganizationId();
    if (orgId) mutateSubscription(fromSubscription(orgId, updated));
  };

  return {
    credits,
    isProPlan,
    isPowerUserPlan,
    isFreePlan,
    isPaidPlan,
    planType,
    billingInterval,
    customTerms,
    topUpPricing,
    planDisplayName,
    refetch,
    applySubscription,
    loading,
    creditsUsedThisPeriod,
    canManageBilling,
    show: () => billed() !== undefined || show(),
  };
};
